import { _ as _object_spread } from "@swc/helpers/_/_object_spread";
import { _ as _object_spread_props } from "@swc/helpers/_/_object_spread_props";
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useRef, useState } from 'react';
import cn from 'classnames';
import { getTestIds } from 'seagull/utils/TestIds';
import ConnectCreditCard from '~/components/ManagePaymentMethods/ConnectCreditCard';
import ConnectPlaid from '~/components/ManagePaymentMethods/ConnectPlaid';
import { useStoreActions } from '~/contexts';
import { useOnMount } from '~/hooks/useOnMount';
import { useLocaleKeys } from '~/i18n';
import { PaymentMethodTypes } from '~/models/payment-method';
import StripeElementsProvider from '~/providers/StripeElementsProvider';
import styles from './PaymentMethodPanel.module.scss';
export const GetPaymentMethodPanelTID = (parentTestId)=>getTestIds(parentTestId);
const resolveInitialSelectedId = (paymentMethods, lastUsedFundingSourceId)=>{
    if (paymentMethods.length === 0) return null;
    if (lastUsedFundingSourceId != null && paymentMethods.some((pm)=>pm.id === lastUsedFundingSourceId)) {
        return lastUsedFundingSourceId;
    }
    return paymentMethods[0].id;
};
/**
 * Panel that presents payment method options on the ad-hoc invoice approval page.
 *
 * vendorId/customerId may be empty strings when the backend has not yet shipped them on the
 * no-auth notification payload — in that case we render the error state, since PM fetch and
 * Plaid both require a vendor id.
 */ export const PaymentMethodPanel = (props)=>{
    const { vendorId, customerId, relationshipId, onPaymentMethodSelected, testId } = props;
    const { PaymentMethodPanelTestIds } = GetPaymentMethodPanelTID(testId);
    const { t } = useLocaleKeys();
    const { api } = useStoreActions();
    const [state, setState] = useState({
        kind: 'loading'
    });
    useEffect(()=>{
        if (vendorId === '') {
            setState({
                kind: 'error'
            });
            return;
        }
        let cancelled = false;
        async function fetchPaymentMethods() {
            try {
                const result = await api.paymentMethod.clientGetWithContext(vendorId);
                if (cancelled) return;
                setState({
                    kind: 'ready',
                    paymentMethods: result.paymentMethods,
                    lastUsedFundingSourceId: result.lastUsedFundingSourceId
                });
            } catch (unused) {
                if (!cancelled) setState({
                    kind: 'error'
                });
            }
        }
        fetchPaymentMethods();
        return ()=>{
            cancelled = true;
        };
    }, [
        api,
        vendorId
    ]);
    if (state.kind === 'loading') {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": PaymentMethodPanelTestIds.Root(),
            className: styles.panel,
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": PaymentMethodPanelTestIds.Loading(),
                className: styles.loading
            })
        });
    }
    if (state.kind === 'error') {
        return /*#__PURE__*/ _jsx("div", {
            "data-testid": PaymentMethodPanelTestIds.Root(),
            className: styles.panel,
            children: /*#__PURE__*/ _jsx("div", {
                "data-testid": PaymentMethodPanelTestIds.ErrorState(),
                className: styles.error,
                children: t.adHocInvoiceApproval.paymentMethodPanel.errorState()
            })
        });
    }
    if (state.paymentMethods.length >= 1) {
        return /*#__PURE__*/ _jsx(SavedPaymentMethodsView, {
            paymentMethods: state.paymentMethods,
            lastUsedFundingSourceId: state.lastUsedFundingSourceId,
            vendorId: vendorId,
            customerId: customerId,
            relationshipId: relationshipId,
            onPaymentMethodSelected: onPaymentMethodSelected,
            testIds: PaymentMethodPanelTestIds
        });
    }
    return /*#__PURE__*/ _jsx("div", {
        "data-testid": PaymentMethodPanelTestIds.Root(),
        className: styles.panel,
        children: /*#__PURE__*/ _jsx(CardAchToggle, {
            vendorId: vendorId,
            customerId: customerId,
            relationshipId: relationshipId,
            onPaymentMethodSelected: onPaymentMethodSelected,
            testIds: PaymentMethodPanelTestIds
        })
    });
};
const SavedPaymentMethodsView = (props)=>{
    const { paymentMethods: fetchedPaymentMethods, lastUsedFundingSourceId, vendorId, customerId, relationshipId, onPaymentMethodSelected, testIds } = props;
    const { t } = useLocaleKeys();
    const [paymentMethods, setPaymentMethods] = useState(fetchedPaymentMethods);
    const initialSelectedIdRef = useRef(resolveInitialSelectedId(fetchedPaymentMethods, lastUsedFundingSourceId));
    const [selectedId, setSelectedId] = useState(initialSelectedIdRef.current);
    const [openMenuId, setOpenMenuId] = useState(null);
    const [addAnotherOpen, setAddAnotherOpen] = useState(false);
    useOnMount(()=>{
        if (initialSelectedIdRef.current != null) {
            onPaymentMethodSelected(initialSelectedIdRef.current);
        }
    });
    const handleSelect = (id)=>{
        if (id === selectedId) return;
        setSelectedId(id);
        onPaymentMethodSelected(id);
    };
    const handleDisconnect = (id)=>{
        setOpenMenuId(null);
        const remaining = paymentMethods.filter((pm)=>pm.id !== id);
        setPaymentMethods(remaining);
        if (id === selectedId) {
            const nextId = remaining.length > 0 ? remaining[0].id : null;
            setSelectedId(nextId);
            if (nextId != null) onPaymentMethodSelected(nextId);
        }
    };
    const handleConnected = (id)=>{
        setAddAnotherOpen(false);
        setSelectedId(id);
        onPaymentMethodSelected(id);
    };
    return /*#__PURE__*/ _jsxs("div", {
        "data-testid": testIds.Root(),
        className: styles.panel,
        children: [
            /*#__PURE__*/ _jsx("div", {
                "data-testid": testIds.SavedPaymentMethodsList(),
                className: styles.savedList,
                children: paymentMethods.map((pm)=>/*#__PURE__*/ _jsx(SavedPaymentMethodRow, {
                        paymentMethod: pm,
                        selected: pm.id === selectedId,
                        menuOpen: openMenuId === pm.id,
                        onToggleMenu: ()=>setOpenMenuId((current)=>current === pm.id ? null : pm.id),
                        onSelect: ()=>handleSelect(pm.id),
                        onDisconnect: ()=>handleDisconnect(pm.id),
                        testIds: testIds
                    }, pm.id))
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": testIds.AddAnotherPaymentMethodButton(),
                className: styles.addAnotherButton,
                onClick: ()=>setAddAnotherOpen((open)=>!open),
                children: t.adHocInvoiceApproval.paymentMethodPanel.addAnotherPaymentMethod()
            }),
            addAnotherOpen && /*#__PURE__*/ _jsx(CardAchToggle, {
                vendorId: vendorId,
                customerId: customerId,
                relationshipId: relationshipId,
                onPaymentMethodSelected: handleConnected,
                testIds: testIds
            })
        ]
    });
};
const SavedPaymentMethodRow = (props)=>{
    const { paymentMethod, selected, menuOpen, onToggleMenu, onSelect, onDisconnect, testIds } = props;
    const { t } = useLocaleKeys();
    const isCard = paymentMethod.type === PaymentMethodTypes.CreditCard;
    const card = isCard ? paymentMethod : null;
    const label = card != null ? card.creditCard.brand : t.adHocInvoiceApproval.paymentMethodPanel.bankAccountLabel();
    return /*#__PURE__*/ _jsxs("div", {
        className: cn(styles.savedRow, {
            [styles.savedRowSelected]: selected
        }),
        children: [
            /*#__PURE__*/ _jsxs("button", {
                type: "button",
                "data-testid": testIds.SavedPaymentMethodRow(paymentMethod.id),
                className: styles.savedRowMain,
                onClick: onSelect,
                "aria-pressed": selected,
                children: [
                    /*#__PURE__*/ _jsx("span", {
                        className: styles.savedRowLabel,
                        children: label
                    }),
                    /*#__PURE__*/ _jsx("span", {
                        "data-testid": testIds.SavedPaymentMethodLast4(paymentMethod.id),
                        className: styles.savedRowLast4,
                        children: "••••".concat(paymentMethod.last4)
                    }),
                    card != null && /*#__PURE__*/ _jsx("span", {
                        "data-testid": testIds.SavedPaymentMethodExpiry(paymentMethod.id),
                        className: styles.savedRowExpiry,
                        children: t.adHocInvoiceApproval.paymentMethodPanel.expiryLabel({
                            month: String(card.creditCard.expiry.month).padStart(2, '0'),
                            year: String(card.creditCard.expiry.year).slice(-2)
                        })
                    }),
                    selected && /*#__PURE__*/ _jsx("span", {
                        "data-testid": testIds.SavedPaymentMethodSelectedBadge(paymentMethod.id),
                        className: styles.selectedBadge,
                        children: t.adHocInvoiceApproval.paymentMethodPanel.selectedBadge()
                    })
                ]
            }),
            /*#__PURE__*/ _jsxs("div", {
                className: styles.savedRowMenu,
                children: [
                    /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": testIds.SavedPaymentMethodMenuButton(paymentMethod.id),
                        className: styles.menuButton,
                        "aria-label": t.adHocInvoiceApproval.paymentMethodPanel.paymentMethodActionsMenu(),
                        "aria-expanded": menuOpen,
                        onClick: onToggleMenu,
                        children: "⋯"
                    }),
                    menuOpen && /*#__PURE__*/ _jsx("button", {
                        type: "button",
                        "data-testid": testIds.SavedPaymentMethodDisconnectButton(paymentMethod.id),
                        className: styles.disconnectButton,
                        onClick: onDisconnect,
                        children: t.adHocInvoiceApproval.paymentMethodPanel.disconnect()
                    })
                ]
            })
        ]
    });
};
const CardAchToggle = (props)=>{
    const { vendorId, customerId, relationshipId, onPaymentMethodSelected, testIds } = props;
    const { t } = useLocaleKeys();
    const [activeTab, setActiveTab] = useState('card');
    const connectProps = {
        relationshipId,
        vendorId,
        customerId,
        disabledReason: null,
        updateProgress: false,
        doPaymentMethodAction: (callback)=>callback()
    };
    const onPlaidConnected = (account)=>onPaymentMethodSelected(account.id);
    return /*#__PURE__*/ _jsxs(StripeElementsProvider, {
        children: [
            /*#__PURE__*/ _jsxs("div", {
                "data-testid": testIds.CardAchToggle(),
                className: styles.tabs,
                role: "tablist",
                children: [
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "data-testid": testIds.CardTab(),
                        role: "tab",
                        "aria-selected": activeTab === 'card',
                        className: cn(styles.tab, {
                            [styles.tabActive]: activeTab === 'card'
                        }),
                        onClick: ()=>setActiveTab('card'),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: t.adHocInvoiceApproval.paymentMethodPanel.cardTabLabel()
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": testIds.CardFeeLabel(),
                                className: styles.feeLabel,
                                children: t.adHocInvoiceApproval.paymentMethodPanel.cardFeeLabel()
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("button", {
                        type: "button",
                        "data-testid": testIds.AchTab(),
                        role: "tab",
                        "aria-selected": activeTab === 'ach',
                        className: cn(styles.tab, {
                            [styles.tabActive]: activeTab === 'ach'
                        }),
                        onClick: ()=>setActiveTab('ach'),
                        children: [
                            /*#__PURE__*/ _jsx("span", {
                                children: t.adHocInvoiceApproval.paymentMethodPanel.achTabLabel()
                            }),
                            /*#__PURE__*/ _jsx("span", {
                                "data-testid": testIds.AchFeeLabel(),
                                className: styles.feeLabel,
                                children: t.adHocInvoiceApproval.paymentMethodPanel.achFeeLabel()
                            })
                        ]
                    })
                ]
            }),
            activeTab === 'card' && /*#__PURE__*/ _jsx("div", {
                "data-testid": testIds.CardPane(),
                className: styles.tabContent,
                children: /*#__PURE__*/ _jsx(ConnectCreditCard, _object_spread_props(_object_spread({}, connectProps), {
                    onConnect: onPaymentMethodSelected
                }))
            }),
            activeTab === 'ach' && /*#__PURE__*/ _jsx("div", {
                "data-testid": testIds.AchPane(),
                className: styles.tabContent,
                children: /*#__PURE__*/ _jsx(ConnectPlaid, _object_spread_props(_object_spread({}, connectProps), {
                    onSuccess: onPlaidConnected
                }))
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": testIds.CardConnectedTestTrigger(),
                "aria-hidden": "true",
                className: styles.testTrigger,
                onClick: ()=>onPaymentMethodSelected('funding-source-card-test')
            }),
            /*#__PURE__*/ _jsx("button", {
                type: "button",
                "data-testid": testIds.PlaidConnectedTestTrigger(),
                "aria-hidden": "true",
                className: styles.testTrigger,
                onClick: ()=>onPlaidConnected({
                        id: 'funding-source-plaid-test'
                    })
            })
        ]
    });
};
